import {
  isLocalPathTemplate,
  type LocalPathProblem,
  type LocalPathTemplateId,
} from '@electron/shared/local-mcp.types';

import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';

import type { ServerTransport } from '@littlebird/axon-mcp';

export type TrustedLocalPathId = LocalPathTemplateId;

export type LocalPathConnectProblem =
  | 'timed_out'
  | 'connection_failed'
  | 'certificate_untrusted'
  | 'save_failed'
  | 'already_connected';

export type LocalPathSetupFailure =
  | { problem: LocalPathProblem; path: string }
  | { problem: LocalPathConnectProblem };

export interface TrustedLocalPathSetup {
  id: TrustedLocalPathId;
  iconKey: TrustedLocalPathId;
  title: string;
  description: string;
  listLabel: string;
  emptyText: string;
  chooseLabel: string;
  addAnotherLabel: string;
  removeLabel: string;
  /** Copy for a folder the check rejected; `{folder}` is the folder's name */
  pathFailureCopy: Readonly<Record<LocalPathProblem, string>>;
  connectFailureCopy: Readonly<Record<LocalPathConnectProblem, string>>;
}

export const LOCAL_PATH_SETUPS: Readonly<
  Record<TrustedLocalPathId, TrustedLocalPathSetup>
> = {
  filesystem: {
    id: 'filesystem',
    iconKey: 'filesystem',
    title: 'Connect Filesystem',
    description:
      'Choose the folders Littlebird can work with. Nothing outside them is available.',
    listLabel: 'Folders Littlebird can access',
    emptyText: 'No folders selected. Choose at least one to continue.',
    chooseLabel: 'Choose a folder',
    addAnotherLabel: 'Add another folder',
    removeLabel: 'Remove folder',
    pathFailureCopy: {
      missing:
        '“{folder}” can’t be found. It may have been moved or renamed. Remove it, or add it again from its new location.',
      permission_denied:
        'Littlebird doesn’t have permission to use “{folder}”. Add it again to grant access.',
      trailing_whitespace:
        'Folder names can’t end with a space. Rename “{folder}” without a space at the end, then add the folder again.',
    },
    connectFailureCopy: {
      timed_out:
        'Filesystem is taking longer than expected to connect. Try again.',
      connection_failed:
        'Filesystem couldn’t connect. Your folders are still selected. Try again.',
      certificate_untrusted:
        'Littlebird couldn’t download Filesystem. Your network inspects secure connections with a certificate Littlebird doesn’t trust. Ask your IT team for help.',
      save_failed:
        'Filesystem connected, but Littlebird couldn’t finish saving it. Try again.',
      already_connected: 'Filesystem is already connected.',
    },
  },
  obsidian: {
    id: 'obsidian',
    iconKey: 'obsidian',
    title: 'Connect Obsidian',
    description:
      'Choose the vaults Littlebird can work with. Nothing outside them is available.',
    listLabel: 'Vaults Littlebird can access',
    emptyText: 'No vaults selected. Choose at least one to continue.',
    chooseLabel: 'Choose a vault',
    addAnotherLabel: 'Add another vault',
    removeLabel: 'Remove vault',
    pathFailureCopy: {
      missing:
        '“{folder}” can’t be found. It may have been moved or renamed. Remove it, or add it again from its new location.',
      permission_denied:
        'Littlebird doesn’t have permission to use “{folder}”. Add it again to grant access.',
      trailing_whitespace:
        'Folder names can’t end with a space. Rename “{folder}” without a space at the end, then add the vault again.',
    },
    connectFailureCopy: {
      timed_out:
        'Obsidian is taking longer than expected to connect. Try again.',
      connection_failed:
        'Obsidian couldn’t connect. Your vaults are still selected. Try again.',
      certificate_untrusted:
        'Littlebird couldn’t download Obsidian. Your network inspects secure connections with a certificate Littlebird doesn’t trust. Ask your IT team for help.',
      save_failed:
        'Obsidian connected, but Littlebird couldn’t finish saving it. Try again.',
      already_connected: 'Obsidian is already connected.',
    },
  },
};

export function getLocalPathSetup(
  id: string | undefined
): TrustedLocalPathSetup | undefined {
  if (!isLocalPathTemplate(id)) return undefined;
  return LOCAL_PATH_SETUPS[id];
}

export function describeLocalPath(localPath: string): {
  name: string;
  parent: string;
} {
  const withoutTrailingSeparators = localPath.replace(/[\\/]+$/, '');
  const displayPath = withoutTrailingSeparators || localPath;
  const separatorIndex = Math.max(
    displayPath.lastIndexOf('/'),
    displayPath.lastIndexOf('\\')
  );
  if (separatorIndex < 0) return { name: displayPath, parent: '' };

  const name = displayPath.slice(separatorIndex + 1) || displayPath;
  let parent = displayPath.slice(0, separatorIndex);
  if (!parent) parent = displayPath.slice(0, 1);
  if (/^[A-Za-z]:$/.test(parent)) parent += '\\';
  return { name, parent };
}

export function describeLocalPathFailure(
  setup: TrustedLocalPathSetup,
  failure: LocalPathSetupFailure
): string {
  if ('path' in failure) {
    const folderName = describeLocalPath(failure.path).name;
    return setup.pathFailureCopy[failure.problem].replace(
      '{folder}',
      () => folderName
    );
  }
  return setup.connectFailureCopy[failure.problem];
}

export function buildLocalPathTransport(
  entry: OnDeviceMcpCatalogEntry,
  paths: readonly string[]
): ServerTransport {
  return {
    kind: 'stdio',
    runtime: entry.runtime,
    packageSpec: entry.package_spec,
    args: [...paths],
  };
}
